import { Form } from 'antd';
import type { Dayjs } from 'dayjs';
import { useEffect, useRef, useState } from 'react';

import { FREQUENCY_MODE } from 'app/typings/core/extranet/inventory/enums/FrequencyMode';

import {
  addWeekday,
  getIsoWeekday,
  resolveCustomWeekdays,
} from '../../utils/weekdays';

import type {
  AvailabilityFormFields,
  AvailabilityFormValues,
  AvailabilityInitialValues,
} from './availabilityForm.types';

type Params = {
  initialValues: AvailabilityInitialValues;
  onSubmit: (values: AvailabilityFormValues) => void;
  onChange?: (values: AvailabilityFormValues | null) => void;
  durationMinutes?: number;
  onWeekdaysSynced?: () => void;
};

type RangeValue = [Dayjs | null, Dayjs | null] | null;

export const useAvailabilityForm = ({
  initialValues,
  onSubmit,
  onChange,
  durationMinutes,
  onWeekdaysSynced,
}: Params) => {
  const [form] = Form.useForm<AvailabilityFormFields>();
  const [timeWindowError, setTimeWindowError] = useState(false);

  const isRecurringLocked =
    (initialValues.frequency ?? FREQUENCY_MODE.NONE) !== FREQUENCY_MODE.NONE;

  const resolveEndTime = (startTime?: Dayjs | null): Dayjs | null => {
    if (!startTime) {
      return null;
    }

    return durationMinutes
      ? startTime.add(durationMinutes, 'minute')
      : initialValues.endTime ?? null;
  };

  const normalizeTimeRange = (
    value?: RangeValue,
    prevValue?: RangeValue
  ): [Dayjs, Dayjs | null] | undefined => {
    if (!value) {
      return undefined;
    }

    const [first, second] = value;
    const prevEnd = prevValue?.[1] ?? null;

    const startTime =
      first && prevEnd && first.isSame(prevEnd, 'minute') ? second : first;

    if (!startTime) {
      return undefined;
    }

    return [startTime, resolveEndTime(startTime)];
  };

  const formInitialValues: AvailabilityFormFields = {
    date: initialValues.date,
    timeRange: normalizeTimeRange(
      initialValues.startTime
        ? [initialValues.startTime, initialValues.endTime ?? null]
        : undefined
    ),
    capacity: initialValues.capacity ?? 1,
    frequency: initialValues.frequency ?? FREQUENCY_MODE.NONE,
    weekdays: resolveCustomWeekdays(initialValues.weekdays),
  };

  const buildValues = (
    fields: Partial<AvailabilityFormFields>
  ): AvailabilityFormValues | null => {
    const [startTime, endTime] = fields.timeRange ?? [];
    const { date } = fields;

    if (!date || !startTime || !endTime) {
      return null;
    }

    const frequency = fields.frequency ?? FREQUENCY_MODE.NONE;
    const rawWeekdays = fields.weekdays ?? [];
    const weekdays =
      frequency === FREQUENCY_MODE.CUSTOM && rawWeekdays.length === 0
        ? [getIsoWeekday(date)]
        : rawWeekdays;

    return {
      date,
      startTime,
      endTime,
      capacity: fields.capacity ?? 1,
      frequency,
      weekdays,
    };
  };

  const onChangeRef = useRef(onChange);

  onChangeRef.current = onChange;

  useEffect(() => {
    onChangeRef.current?.(buildValues(form.getFieldsValue()));

    return () => onChangeRef.current?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / unmount only.
  }, []);

  const handleValuesChange = (
    changed: Partial<AvailabilityFormFields>,
    allValues: Partial<AvailabilityFormFields>
  ) => {
    let values = allValues;

    if (changed.frequency === FREQUENCY_MODE.CUSTOM) {
      const weekdays = resolveCustomWeekdays(
        form.getFieldsValue(true).weekdays
      );

      form.setFieldsValue({ weekdays });
      values = { ...values, weekdays };
    }

    if (changed.date && values.frequency === FREQUENCY_MODE.CUSTOM) {
      const current = values.weekdays ?? [];
      const nextWeekdays = addWeekday(current, getIsoWeekday(changed.date));

      if (nextWeekdays !== current) {
        form.setFieldsValue({ weekdays: nextWeekdays });
        values = { ...values, weekdays: nextWeekdays };
        onWeekdaysSynced?.();
      }
    }

    const built = buildValues(values);

    if (built) {
      setTimeWindowError(false);
    }

    onChangeRef.current?.(built);
  };

  const switchToNoRepeat = () => {
    form.setFieldsValue({ frequency: FREQUENCY_MODE.NONE });
    onChangeRef.current?.(buildValues(form.getFieldsValue()));
  };

  const handleSave = () => {
    const values = buildValues(form.getFieldsValue(true));

    if (!values) {
      setTimeWindowError(true);

      return;
    }

    setTimeWindowError(false);
    onSubmit(values);
  };

  return {
    form,
    formInitialValues,
    isRecurringLocked,
    normalizeTimeRange,
    handleValuesChange,
    handleSave,
    switchToNoRepeat,
    timeWindowError,
  };
};
